import { useState, useEffect, useRef } from "react"
import { X, Loader2, Camera, Plus } from "lucide-react"
import { Button } from "components/ui/button"
import { Input } from "components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select"
import { createUser, updateUser, getUserTags, createUserTag } from "services/users"
import api from "api/axios"
import { getTagChipStyles } from "utils/tagHelpers"
import { useTheme } from "contexts/ThemeContext"
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "components/ui/sheet"
import "./team-responsive.css"

const TAG_COLORS = [
  '#7b3fff', // viola
  '#38bdf8', // azzurro
  '#34d399', // verde
  '#fbbf24', // giallo
  '#fb7185', // rosa
  '#e879f9', // magenta
]

interface UserTag {
  id: number
  name: string
  color: string
}

interface AddUserModalProps {
  isOpen: boolean
  onClose: () => void
  editMode?: boolean
  userId?: number
  initialData?: {
    nome: string
    cognome: string
    email: string
    ruolo: string
    profilePhotoUrl?: string | null
    tags?: UserTag[]
  }
}

interface FormErrors {
  nome?: string
  cognome?: string
  email?: string
  ruolo?: string
  password?: string
  confirmPassword?: string
  general?: string
}

const COMMON_PASSWORDS = [
  'password', 'password1', 'password123', '12345678', '123456789',
  'qwerty123', 'abc12345', 'admin123', 'letmein', 'welcome1'
]

export function AddUserModal({ isOpen, onClose, editMode = false, userId, initialData }: AddUserModalProps) {
  const { theme } = useTheme()
  const [formData, setFormData] = useState({
    nome: "",
    cognome: "",
    email: "",
    ruolo: "",
    password: "",
    confirmPassword: "",
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [isLoading, setIsLoading] = useState(false)

  // Avatar state
  const [pendingPhotoFile, setPendingPhotoFile] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const photoInputRef = useRef<HTMLInputElement>(null)

  // Tag state
  const [availableTags, setAvailableTags] = useState<UserTag[]>([])
  const [selectedTags, setSelectedTags] = useState<UserTag[]>([])
  const [tagInputValue, setTagInputValue] = useState("")
  const [showTagDropdown, setShowTagDropdown] = useState(false)
  const tagInputRef = useRef<HTMLInputElement>(null)
  const tagDropdownRef = useRef<HTMLDivElement>(null)

  // Load tags on mount
  useEffect(() => {
    if (!isOpen) return
    let active = true
    const loadTags = async () => {
      try {
        const res = await getUserTags()
        if (active && res.data && Array.isArray(res.data)) {
          setAvailableTags(res.data)
        }
      } catch (error) {
        console.error("Error loading user tags:", error)
      }
    }
    loadTags()
    return () => { active = false }
  }, [isOpen])

  // Reset form when modal opens/closes or initialData changes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        nome: initialData?.nome || "",
        cognome: initialData?.cognome || "",
        email: initialData?.email || "",
        ruolo: initialData?.ruolo || "",
        password: "",
        confirmPassword: "",
      })
      setErrors({})
      setPendingPhotoFile(null)
      setPhotoPreview(null)
      setSelectedTags(initialData?.tags || [])
      setTagInputValue("")
      setShowTagDropdown(false)
    }
  }, [isOpen, initialData])

  // Click outside handler for tag dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tagDropdownRef.current &&
        !tagDropdownRef.current.contains(event.target as Node) &&
        tagInputRef.current &&
        !tagInputRef.current.contains(event.target as Node)
      ) {
        setShowTagDropdown(false)
      }
    }

    if (showTagDropdown) {
      document.addEventListener("mousedown", handleClickOutside)
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [showTagDropdown])

  // Avatar handlers
  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setPendingPhotoFile(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const getAvatarInitials = (): string => {
    const f = formData.nome?.charAt(0)?.toUpperCase() || ""
    const l = formData.cognome?.charAt(0)?.toUpperCase() || ""
    return f + l || ""
  }

  // Tag handlers
  const filteredTags = availableTags.filter(
    (tag) =>
      tag.name.toLowerCase().includes(tagInputValue.toLowerCase()) &&
      !selectedTags.some((st) => st.id === tag.id)
  )

  const showCreateOption =
    tagInputValue.trim() &&
    !availableTags.some((t) => t.name.toLowerCase() === tagInputValue.toLowerCase())

  const handleTagSelect = (tag: UserTag) => {
    setSelectedTags((prev) => [...prev, tag])
    setTagInputValue("")
    setShowTagDropdown(false)
  }

  const handleTagRemove = (tagId: number) => {
    setSelectedTags((prev) => prev.filter((t) => t.id !== tagId))
  }

  const handleCreateTag = async () => {
    if (!tagInputValue.trim()) return
    try {
      const colorIndex = availableTags.length % TAG_COLORS.length
      const newTagData = {
        name: tagInputValue.trim(),
        color: TAG_COLORS[colorIndex],
      }
      const res = await createUserTag(newTagData)
      const newTag = res.data
      setAvailableTags((prev) => [...prev, newTag])
      setSelectedTags((prev) => [...prev, newTag])
      setTagInputValue("")
      setShowTagDropdown(false)
    } catch (error) {
      console.error("Error creating tag:", error)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const validatePassword = (password: string): string | undefined => {
    if (!password) return "La password è obbligatoria"
    if (password.length < 8) return "La password deve avere almeno 8 caratteri"
    if (COMMON_PASSWORDS.includes(password.toLowerCase())) return "Questa password è troppo comune"
    if (!/\d/.test(password) && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return "La password deve contenere almeno un numero o un carattere speciale"
    }
    return undefined
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.nome.trim()) newErrors.nome = "Il nome è obbligatorio"
    if (!formData.cognome.trim()) newErrors.cognome = "Il cognome è obbligatorio"
    if (!formData.email.trim()) {
      newErrors.email = "L'email è obbligatoria"
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Inserisci un'email valida"
    }
    if (!formData.ruolo) newErrors.ruolo = "Seleziona un ruolo"

    if (!editMode) {
      const passwordError = validatePassword(formData.password)
      if (passwordError) newErrors.password = passwordError
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = "Conferma la password"
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = "Le password non corrispondono"
      }
    } else {
      if (formData.password || formData.confirmPassword) {
        const passwordError = validatePassword(formData.password)
        if (passwordError) newErrors.password = passwordError
        if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = "Le password non corrispondono"
        }
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    setIsLoading(true)
    setErrors({})

    try {
      const userData: Record<string, any> = {
        first_name: formData.nome,
        last_name: formData.cognome,
        email: formData.email,
        role: formData.ruolo,
        tag_ids: selectedTags.map(t => t.id),
      }
      if (!editMode) {
        userData.username = `${formData.nome.toLowerCase()}.${formData.cognome.toLowerCase()}`
      }
      if (formData.password) {
        userData.password = formData.password
      }

      if (pendingPhotoFile) {
        // Use FormData for multipart upload
        const formDataObj = new FormData()
        Object.entries(userData).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            if (Array.isArray(value)) {
              value.forEach((v) => formDataObj.append(key, String(v)))
            } else {
              formDataObj.append(key, String(value))
            }
          }
        })
        formDataObj.append('profile_photo', pendingPhotoFile)

        if (editMode && userId) {
          await api.patch(`api/accounts/users/${userId}/`, formDataObj)
        } else {
          await api.post('api/accounts/users/', formDataObj)
        }
      } else {
        if (editMode && userId) {
          await updateUser(userId, userData)
        } else {
          await createUser(userData)
        }
      }

      onClose()
    } catch (error: any) {
      console.error("Error saving user:", error)
      if (error.response?.data) {
        const apiErrors = error.response.data
        const newErrors: FormErrors = {}
        if (apiErrors.email) newErrors.email = Array.isArray(apiErrors.email) ? apiErrors.email[0] : apiErrors.email
        if (apiErrors.username) newErrors.general = Array.isArray(apiErrors.username) ? apiErrors.username[0] : apiErrors.username
        if (apiErrors.password) newErrors.password = Array.isArray(apiErrors.password) ? apiErrors.password[0] : apiErrors.password
        if (apiErrors.role) newErrors.ruolo = Array.isArray(apiErrors.role) ? apiErrors.role[0] : apiErrors.role
        if (apiErrors.detail) newErrors.general = apiErrors.detail
        if (apiErrors.non_field_errors) newErrors.general = Array.isArray(apiErrors.non_field_errors) ? apiErrors.non_field_errors[0] : apiErrors.non_field_errors
        setErrors(Object.keys(newErrors).length > 0 ? newErrors : { general: "Errore durante il salvataggio. Riprova." })
      } else {
        setErrors({ general: "Errore di connessione. Verifica la tua connessione e riprova." })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const ErrorMessage = ({ message }: { message?: string }) => {
    if (!message) return null
    return <p className="text-rose-500 text-xs mt-1">{message}</p>
  }

  return (
    <Sheet open={isOpen} onOpenChange={(open) => { if (!open && !isLoading) onClose() }}>
      <SheetContent className="team-sheet w-full sm:max-w-[400px] h-[100dvh] gap-0 p-0 bg-[var(--hexa-modal-bg)] border-[rgba(106,77,255,0.25)]">
        <SheetDescription className="sr-only">Dati anagrafici, ruolo e credenziali del membro del team.</SheetDescription>
        {/* Header */}
        <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-[rgba(106,77,255,0.25)]">
          <SheetTitle className="text-[var(--hexa-text-primary)] font-semibold text-sm">{editMode ? "Modifica Utente" : "Aggiungi Utente"}</SheetTitle>
          <button
            onClick={onClose}
            disabled={isLoading}
            aria-label="Chiudi utente"
            className="text-[var(--hexa-text-secondary)] hover:text-[var(--hexa-text-primary)] transition-colors disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 min-w-0 overflow-y-auto p-4 space-y-4 scrollbar-hexa">
          {/* General Error */}
          {errors.general && (
            <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-3">
              <p className="text-rose-500 text-sm">{errors.general}</p>
            </div>
          )}

          {/* Avatar Upload */}
          <div className="flex justify-center">
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              className="hidden"
            />
            <div
              onClick={() => photoInputRef.current?.click()}
              className="w-[52px] h-[52px] rounded-full border-2 border-dashed border-[var(--hexa-purple)] flex items-center justify-center cursor-pointer overflow-hidden hover:border-[var(--hexa-purple-hover)] transition bg-[var(--hexa-surface-elevated)]"
            >
              {photoPreview || (editMode && initialData?.profilePhotoUrl) ? (
                <img
                  src={photoPreview || initialData?.profilePhotoUrl || ""}
                  alt="Avatar"
                  className="w-full h-full object-cover"
                />
              ) : getAvatarInitials() ? (
                <span className="text-sm font-semibold text-[var(--hexa-text-secondary)]">
                  {getAvatarInitials()}
                </span>
              ) : (
                <Camera className="w-5 h-5 text-[var(--hexa-purple)]" />
              )}
            </div>
          </div>

          {/* Nome + Cognome */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[var(--hexa-text-primary)] text-xs mb-1 block">
                Nome <span className="text-rose-500">*</span>
              </label>
              <Input
                type="text"
                placeholder="Inserisci il nome"
                value={formData.nome}
                onChange={(e) => handleInputChange("nome", e.target.value)}
                disabled={isLoading}
                className={`bg-[var(--hexa-button-cancel)] border rounded-lg px-3 py-2 text-[var(--hexa-text-primary)] placeholder:text-[var(--hexa-text-muted)] text-xs ${errors.nome ? 'border-rose-500' : 'border-transparent'}`}
              />
              <ErrorMessage message={errors.nome} />
            </div>
            <div>
              <label className="text-[var(--hexa-text-primary)] text-xs mb-1 block">
                Cognome <span className="text-rose-500">*</span>
              </label>
              <Input
                type="text"
                placeholder="Inserisci il cognome"
                value={formData.cognome}
                onChange={(e) => handleInputChange("cognome", e.target.value)}
                disabled={isLoading}
                className={`bg-[var(--hexa-button-cancel)] border rounded-lg px-3 py-2 text-[var(--hexa-text-primary)] placeholder:text-[var(--hexa-text-muted)] text-xs ${errors.cognome ? 'border-rose-500' : 'border-transparent'}`}
              />
              <ErrorMessage message={errors.cognome} />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="text-[var(--hexa-text-primary)] text-xs mb-1 block">
              Email <span className="text-rose-500">*</span>
            </label>
            <Input
              type="email"
              placeholder="esempio@azienda.it"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              disabled={isLoading}
              className={`bg-[var(--hexa-button-cancel)] border rounded-lg px-3 py-2 text-[var(--hexa-text-primary)] placeholder:text-[var(--hexa-text-muted)] w-full text-xs ${errors.email ? 'border-rose-500' : 'border-transparent'}`}
            />
            <ErrorMessage message={errors.email} />
          </div>

          {/* Ruolo */}
          <div>
            <label className="text-[var(--hexa-text-primary)] text-xs mb-1 block">
              Ruolo <span className="text-rose-500">*</span>
            </label>
            <Select value={formData.ruolo} onValueChange={(value) => handleInputChange("ruolo", value)} disabled={isLoading}>
              <SelectTrigger className={`w-full bg-[var(--hexa-button-cancel)] border rounded-lg px-3 py-2 text-[var(--hexa-text-primary)] h-auto text-xs ${errors.ruolo ? 'border-rose-500' : 'border-transparent'}`}>
                <SelectValue placeholder="Seleziona un ruolo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="worker">Worker</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
              </SelectContent>
            </Select>
            <ErrorMessage message={errors.ruolo} />
          </div>

          {/* Tag */}
          <div>
            <label className="text-[var(--hexa-text-primary)] text-xs mb-1 block">
              Tag
            </label>
            <div className="relative">
              <div className={`w-full min-h-[34px] rounded-lg bg-[var(--hexa-button-cancel)] border border-transparent px-2.5 flex flex-wrap items-center gap-1.5 focus-within:border-[rgba(106,77,255,0.25)] transition ${selectedTags.length > 0 ? 'py-1.5' : 'py-1.5'}`}>
                {/* Selected tag chips */}
                {selectedTags.map((tag) => (
                  <span
                    key={tag.id}
                    className="team-tag inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium border"
                    style={getTagChipStyles(tag.color, theme)}
                  >
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: tag.color }}
                    />
                    {tag.name}
                    <button
                      type="button"
                      onClick={() => handleTagRemove(tag.id)}
                      className="ml-0.5 hover:opacity-70 transition"
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </span>
                ))}

                {/* Tag search input */}
                <input
                  ref={tagInputRef}
                  type="text"
                  placeholder={selectedTags.length === 0 ? "Cerca o crea tag..." : ""}
                  value={tagInputValue}
                  onChange={(e) => {
                    setTagInputValue(e.target.value)
                    setShowTagDropdown(true)
                  }}
                  onFocus={() => setShowTagDropdown(true)}
                  disabled={isLoading}
                  className="flex-1 min-w-[80px] bg-transparent border-none text-[var(--hexa-text-primary)] text-xs focus:outline-none placeholder:text-[var(--hexa-text-muted)]"
                />
              </div>

              {/* Tag dropdown */}
              {showTagDropdown && (tagInputValue || filteredTags.length > 0) && (
                <div
                  ref={tagDropdownRef}
                  className="absolute z-50 w-full mt-1 bg-[var(--hexa-modal-bg)] border border-[rgba(106,77,255,0.25)] rounded-lg shadow-xl max-h-32 overflow-y-auto scrollbar-hexa"
                >
                  {filteredTags.map((tag) => (
                    <div
                      key={tag.id}
                      onClick={() => handleTagSelect(tag)}
                      className="px-2.5 py-1.5 hover:bg-[var(--hexa-button-cancel)] cursor-pointer flex items-center gap-2"
                    >
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: tag.color }}
                      />
                      <span className="text-xs text-[var(--hexa-text-primary)]">{tag.name}</span>
                    </div>
                  ))}

                  {showCreateOption && (
                    <div
                      onClick={handleCreateTag}
                      className="px-2.5 py-1.5 hover:bg-[var(--hexa-button-cancel)] cursor-pointer flex items-center gap-2 border-t border-[rgba(106,77,255,0.25)]"
                    >
                      <Plus className="w-3 h-3 text-[var(--hexa-purple)]" />
                      <span className="text-xs text-[var(--hexa-text-primary)]">
                        Crea "<span className="font-medium text-[var(--hexa-purple)]">{tagInputValue.trim()}</span>"
                      </span>
                    </div>
                  )}

                  {filteredTags.length === 0 && !showCreateOption && tagInputValue && (
                    <div className="px-2.5 py-1.5 text-xs text-[var(--hexa-text-secondary)]">
                      Nessun tag trovato
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Password */}
          <div>
            <label className="text-[var(--hexa-text-primary)] text-xs mb-1 block">
              Password {!editMode && <span className="text-rose-500">*</span>}
              {editMode && <span className="text-[var(--hexa-text-secondary)] text-[10px] ml-1">(lascia vuoto per non modificare)</span>}
            </label>
            <Input
              type="password"
              placeholder="Inserisci password"
              value={formData.password}
              onChange={(e) => handleInputChange("password", e.target.value)}
              disabled={isLoading}
              className={`bg-[var(--hexa-button-cancel)] border rounded-lg px-3 py-2 text-[var(--hexa-text-primary)] placeholder:text-[var(--hexa-text-muted)] w-full text-xs ${errors.password ? 'border-rose-500' : 'border-transparent'}`}
            />
            <ErrorMessage message={errors.password} />
            {!editMode && (
              <p className="text-[var(--hexa-text-secondary)] text-[10px] mt-1">
                Minimo 8 caratteri, non troppo comune, con almeno un numero o carattere speciale
              </p>
            )}
          </div>

          {/* Conferma Password */}
          <div>
            <label className="text-[var(--hexa-text-primary)] text-xs mb-1 block">
              Conferma password {!editMode && <span className="text-rose-500">*</span>}
            </label>
            <Input
              type="password"
              placeholder="Conferma password"
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
              disabled={isLoading}
              className={`bg-[var(--hexa-button-cancel)] border rounded-lg px-3 py-2 text-[var(--hexa-text-primary)] placeholder:text-[var(--hexa-text-muted)] w-full text-xs ${errors.confirmPassword ? 'border-rose-500' : 'border-transparent'}`}
            />
            <ErrorMessage message={errors.confirmPassword} />
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 border-t border-[rgba(106,77,255,0.25)] px-4 py-3">
          <div className="flex gap-2.5">
            <div className="flex-1 bg-[var(--hexa-button-cancel)] rounded-lg p-2.5 flex items-center justify-center border border-[rgba(106,77,255,0.25)]">
              <Button
                variant="ghost"
                onClick={onClose}
                disabled={isLoading}
                className="w-full text-[var(--hexa-text-secondary)] hover:text-[var(--hexa-text-primary)] text-xs"
              >
                Annulla
              </Button>
            </div>
            <div className="flex-1 rounded-lg flex items-center justify-center">
              <Button
                onClick={handleSubmit}
                disabled={isLoading}
                className="w-full h-full bg-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-hover)] text-white rounded-lg py-2.5 disabled:opacity-50 text-xs"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  editMode ? "Salva modifiche" : "Salva"
                )}
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
